import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ensureCsvPassword } from "@/lib/csvPassword";
import { getCemigRule, cemigCommission, clampCemigDiscount } from "@/lib/cemigRules";
import { Calculator, Download, Zap, TrendingUp, DollarSign, Edit, UserPlus, Trash2, Upload, FileDown, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Simulator({ user, onLogout }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [importing, setImporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Configurações dinâmicas carregadas do backend
  const [distributorDiscounts, setDistributorDiscounts] = useState({});
  const [distributorMethods, setDistributorMethods] = useState({});
  // Modelo de cálculo: "with_taxes" (CEMIG) não pede impostos; "without_taxes" (CELESC) pede ICMS/PIS/COFINS
  const WITH_TAXES_FALLBACK = ["CEMIG", "EQUATORIAL-GO", "ENERGISA-MG"];
  const isWithTaxes = (dist) => {
    const m = distributorMethods[dist];
    return m ? m === "with_taxes" : WITH_TAXES_FALLBACK.includes(dist);
  };
  const [distributorCommissions, setDistributorCommissions] = useState({});
  const [autoconsumoDiscounts, setAutoconsumoDiscounts] = useState({});
  const [aiFilledFields, setAiFilledFields] = useState([]);
  const [aiExtracting, setAiExtracting] = useState(false);
  const aiFileInputRef = useRef(null);

  // Tarifas por bandeira — base hardcoded (fallback) + overrides vindos da API
  // de configurações de distribuidoras (campos `green`, `yellow`, `red1`,
  // `red2`). Quando o admin cria uma nova distribuidora em /distributor-settings
  // com as tarifas preenchidas, elas aparecem aqui automaticamente.
  const FALLBACK_TARIFFS_BY_FLAG = {
    verde: {
      "CEMIG": 1.1558589,
      "RGE": 0.8222,
      "CELESC": 0.69568,
      "COPEL": 0.64242,
      "CEEE-Equatorial": 0.8222,
      "CERBRANORTE": 0.6314,
      "COELBA-BA": 0.83771,
      "EQUATORIAL-GO": 1.206169,
      "ENERGISA-MT": 1.206169,
      "ENERGISA-MS": 1.206169,
      "ENERGISA-MG": 0.95
    },
    amarela: {
      "CEMIG": 1.1794483,
      "RGE": 0.84105,
      "CELESC": 0.71453,
      "COPEL": 0.66127,
      "CEEE-Equatorial": 0.84105,
      "CERBRANORTE": 0.65025,
      "COELBA-BA": 0.85656,
      "EQUATORIAL-GO": 1.230395,
      "ENERGISA-MT": 1.230395,
      "ENERGISA-MS": 1.230395,
      "ENERGISA-MG": 0.97
    },
    vermelha1: {
      "CEMIG": 1.2116939,
      "RGE": 0.86683,
      "CELESC": 0.74031,
      "COPEL": 0.68705,
      "CEEE-Equatorial": 0.86683,
      "CERBRANORTE": 0.67603,
      "COELBA-BA": 0.88234,
      "EQUATORIAL-GO": 1.263527,
      "ENERGISA-MT": 1.263527,
      "ENERGISA-MS": 1.263527,
      "ENERGISA-MG": 1.00
    },
    vermelha2: {
      "CEMIG": 1.2543959,
      "RGE": 0.90097,
      "CELESC": 0.77445,
      "COPEL": 0.72119,
      "CEEE-Equatorial": 0.90097,
      "CERBRANORTE": 0.71017,
      "COELBA-BA": 0.91648,
      "EQUATORIAL-GO": 1.307404,
      "ENERGISA-MT": 1.307404,
      "ENERGISA-MS": 1.307404,
      "ENERGISA-MG": 1.04
    }
  };
  const [tariffsByFlag, setTariffsByFlag] = useState(FALLBACK_TARIFFS_BY_FLAG);

  // Tarifas específicas para Autoconsumo Remoto — base hardcoded (fallback) +
  // overrides vindos da API (campo `autoconsumo`).
  const FALLBACK_TARIFFS_AUTOCONSUMO = {
    verde: {
      "COPEL": 0.85227,
      "CELESC": 0.85509,
      "RGE": 1.04466668,
      "CEEE-Equatorial": 1.025115,
      "CERBRANORTE": 0.76075
    },
    amarela: {
      "COPEL": 0.875427225,
      "CELESC": 0.878247225,
      "CERBRANORTE": 0.783907225,
      "RGE": 1.067823905,
      "CEEE-Equatorial": 1.048272
    },
    vermelha1: {
      "COPEL": 0.907097955,
      "CELESC": 0.909917955,
      "CERBRANORTE": 0.815577955,
      "RGE": 1.099494635,
      "CEEE-Equatorial": 1.079943
    },
    vermelha2: {
      "COPEL": 0.949038945,
      "CELESC": 0.951858945,
      "CERBRANORTE": 0.857518945,
      "RGE": 1.141435625,
      "CEEE-Equatorial": 1.121884
    }
  };
  const [tariffsAutoconsumo, setTariffsAutoconsumo] = useState(FALLBACK_TARIFFS_AUTOCONSUMO);
  
  const [formData, setFormData] = useState({
    cooperative_name: "",
    installation_number: "",
    distributor: "",
    compensation_type: "Geração Compartilhada",
    tariff_flag: "",
    phone: "",
    consumption: "",
    current_tariff: "",
    discount_percentage: "",
    partner_commission: "",
    total_bill: "",
    icms: "",
    pis: "",
    cofins: "",
    public_lighting: "",
    others: ""
  });

  useEffect(() => {
    fetchSimulations();
    fetchDistributorSettings();
  }, []);

  // Ler fatura por IA (Gemini 3 Flash via Emergent LLM Key)
  const handleAIExtract = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Envie um PDF, JPG, PNG ou WebP.");
      e.target.value = "";
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Arquivo excede 15 MB.");
      e.target.value = "";
      return;
    }
    setAiExtracting(true);
    const toastId = toast.loading("Lendo fatura com IA…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axiosInstance.post("/simulations/extract-invoice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const ex = resp?.data?.extracted || {};
      // NOVA REGRA:
      //   - Para EQUATORIAL-GO / ENERGISA-MT / ENERGISA-MS → usa a tarifa
      //     lida da fatura (o sistema NÃO tem tarifa de referência confiável
      //     para essas regiões).
      //   - Para TODAS as outras distribuidoras → usa a tarifa cadastrada
      //     no sistema (ignora a tarifa extraída pela IA).
      const INVOICE_TARIFF_DISTRIBUTORS = ["EQUATORIAL-GO", "ENERGISA-MT", "ENERGISA-MS"];
      const distRaw = (ex.distributor || "").trim().toUpperCase();
      const useInvoiceTariff = INVOICE_TARIFF_DISTRIBUTORS.includes(distRaw);

      const patch = {};
      const filled = [];
      const setIf = (key, val) => {
        if (val === null || val === undefined || val === "") return;
        patch[key] = typeof val === "number" ? String(val) : String(val);
        filled.push(key);
      };
      setIf("cooperative_name", ex.cooperative_name);
      setIf("installation_number", ex.installation_number);
      setIf("phone", ex.phone);
      setIf("consumption", ex.consumption);
      if (useInvoiceTariff) {
        setIf("current_tariff", ex.current_tariff);
      }
      setIf("total_bill", ex.total_bill);
      setIf("icms", ex.icms);
      setIf("pis", ex.pis);
      setIf("cofins", ex.cofins);
      setIf("public_lighting", ex.public_lighting);
      setIf("others", ex.others);

      // Distribuidora: se aparecer no dropdown, seleciona (aplica desconto/tarifa do sistema)
      const known = Object.keys(distributorDiscounts);
      const match = known.find((d) => d.toUpperCase() === distRaw);
      if (match) {
        if (useInvoiceTariff) {
          // Mantém a tarifa lida da fatura — apenas seta a distribuidora e o desconto/comissão do sistema
          setFormData((prev) => ({
            ...prev,
            ...patch,
            distributor: match,
            discount_percentage: distributorDiscounts[match] || prev.discount_percentage,
            partner_commission: distributorCommissions[match] || prev.partner_commission,
          }));
        } else {
          // Para as demais, aplica handleDistributorChange (traz tarifa do sistema)
          handleDistributorChange(match);
          setFormData((prev) => ({ ...prev, ...patch, distributor: match }));
        }
        filled.push("distributor");
      } else {
        setFormData((prev) => ({ ...prev, ...patch }));
      }
      setAiFilledFields(filled);
      const extraNote = useInvoiceTariff
        ? ` (Tarifa da fatura mantida — ${distRaw})`
        : (match ? ` (Tarifa carregada do sistema — ${distRaw})` : "");
      toast.success(`IA preencheu ${filled.length} campo(s). Revise antes de calcular.${extraNote}`, {
        id: toastId,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Erro ao ler fatura";
      toast.error(String(msg), { id: toastId });
    } finally {
      setAiExtracting(false);
      if (aiFileInputRef.current) aiFileInputRef.current.value = "";
    }
  };

  const fetchDistributorSettings = async () => {
    try {
      const res = await axiosInstance.get("/distributor-settings");
      const discounts = {};
      const commissions = {};
      const autoDisc = {};
      const methods = {};
      // Build tariff tables on top of the hardcoded fallback so new
      // distributors created in the settings panel are picked up.
      const tFlag = JSON.parse(JSON.stringify(FALLBACK_TARIFFS_BY_FLAG));
      const tAuto = JSON.parse(JSON.stringify(FALLBACK_TARIFFS_AUTOCONSUMO));
      for (const item of res.data) {
        const name = item.distributor;
        discounts[name] = item.discount;
        commissions[name] = item.simulator_commission;
        autoDisc[name] = item.autoconsumo_discount || 0;
        if (item.method) methods[name] = item.method;
        // Per-flag tariffs from the settings panel (only if > 0)
        if (item.green && item.green > 0) tFlag.verde[name] = item.green;
        if (item.yellow && item.yellow > 0) tFlag.amarela[name] = item.yellow;
        if (item.red1 && item.red1 > 0) tFlag.vermelha1[name] = item.red1;
        if (item.red2 && item.red2 > 0) tFlag.vermelha2[name] = item.red2;
        // Autoconsumo tariff (single value) is applied to all flags
        if (item.autoconsumo && item.autoconsumo > 0) {
          tAuto.verde[name] = item.autoconsumo;
          // If amarela/red autoconsumo not provided, derive a flat fallback
          if (!tAuto.amarela[name]) tAuto.amarela[name] = item.autoconsumo;
          if (!tAuto.vermelha1[name]) tAuto.vermelha1[name] = item.autoconsumo;
          if (!tAuto.vermelha2[name]) tAuto.vermelha2[name] = item.autoconsumo;
        }
      }
      setDistributorDiscounts(discounts);
      setDistributorMethods(methods);
      setDistributorCommissions(commissions);
      setAutoconsumoDiscounts(autoDisc);
      setTariffsByFlag(tFlag);
      setTariffsAutoconsumo(tAuto);
    } catch (error) {
      console.error("Erro ao carregar configurações de distribuidoras:", error);
    }
  };

  // Função para atualizar desconto e tarifa quando distribuidora mudar
  const handleDistributorChange = (distributor) => {
    const isAutoconsumo = (formData.compensation_type || '').startsWith('Autoconsumo');
    const discount = isAutoconsumo && autoconsumoDiscounts[distributor]
      ? autoconsumoDiscounts[distributor]
      : (distributorDiscounts[distributor] || "");
    
    const tariff = isAutoconsumo && tariffsAutoconsumo.verde[distributor]
      ? tariffsAutoconsumo.verde[distributor]
      : (tariffsByFlag.verde[distributor] || "");
    
    let commission = distributorCommissions[distributor] || "";
    if (distributor === "CEMIG") {
      commission = cemigCommission(discount, cemigRule);
    }
    
    setFormData(prev => ({
      ...prev,
      distributor: distributor,
      discount_percentage: discount,
      partner_commission: commission,
      current_tariff: tariff
    }));
  };

  // Atualizar tarifa e desconto quando tipo de compensação mudar
  const handleCompensationTypeChange = (type) => {
    const dist = formData.distributor;
    let tariff = formData.current_tariff;
    let discount = formData.discount_percentage;
    
    if (dist) {
      tariff = (type || '').startsWith('Autoconsumo') && tariffsAutoconsumo.verde[dist]
        ? tariffsAutoconsumo.verde[dist]
        : (tariffsByFlag.verde[dist] || tariff);
      discount = (type || '').startsWith('Autoconsumo') && autoconsumoDiscounts[dist]
        ? autoconsumoDiscounts[dist]
        : (distributorDiscounts[dist] || discount);
    }
    setFormData(prev => ({
      ...prev,
      compensation_type: type,
      current_tariff: tariff,
      discount_percentage: discount,
      ...((type || '').startsWith('Autoconsumo') ? { pis: "", cofins: "" } : {})
    }));
  };

  const fetchSimulations = async () => {
    try {
      const response = await axiosInstance.get("/simulations");
      setSimulations(response.data);
    } catch (error) {
      toast.error("Erro ao carregar simulações");
    } finally {
      setLoading(false);
    }
  };

  const handleSimulate = async (e) => {
    e.preventDefault();
    setCalculating(true);
    setResult(null);
    
    try {
      // Calcular comissão internamente baseada na distribuidora
      let commission = distributorCommissions[formData.distributor] || 0;
      if (formData.distributor === "CEMIG") {
        commission = cemigCommission(formData.discount_percentage || 0, cemigRule);
      }
      
      const payload = {
        cooperative_name: formData.cooperative_name,
        installation_number: formData.installation_number,
        distributor: formData.distributor,
        compensation_type: formData.compensation_type,
        phone: formData.phone,
        consumption: parseFloat(formData.consumption),
        current_tariff: parseFloat(formData.current_tariff),
        discount_percentage: parseFloat(formData.discount_percentage),
        partner_commission: commission,
        total_bill: parseFloat(formData.total_bill) || 0,
        icms: parseFloat(formData.icms) || 0,
        pis: parseFloat(formData.pis) || 0,
        cofins: parseFloat(formData.cofins) || 0,
        public_lighting: parseFloat(formData.public_lighting) || 0,
        others: parseFloat(formData.others) || 0
      };
      
      const response = await axiosInstance.post("/simulations", payload);
      setResult(response.data);
      
      toast.success("Simulação realizada com sucesso!");
      fetchSimulations();
      
      // Reset form
      setFormData({
        cooperative_name: "",
        installation_number: "",
        distributor: "",
        tariff_flag: "",
        phone: "",
        consumption: "",
        current_tariff: "",
        discount_percentage: "",
        partner_commission: "",
        total_bill: "",
        icms: "",
        pis: "",
        cofins: "",
        public_lighting: "",
        others: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao realizar simulação");
    } finally {
      setCalculating(false);
    }
  };

  const handleDownloadPDF = async (simulationId) => {
    try {
      const response = await axiosInstance.get(`/simulations/${simulationId}/pdf`, {
        responseType: 'blob'
      });
      
      // Usar o Content-Type da resposta para criar o Blob corretamente
      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `simulacao_${simulationId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar PDF");
    }
  };

  const handleEditSimulation = (sim) => {
    // Carregar TODOS os dados da simulação no formulário
    setFormData({
      cooperative_name: sim.cooperative_name || '',
      installation_number: sim.installation_number || '',
      distributor: sim.distributor || '',
      tariff_flag: '',
      phone: sim.phone || '',
      consumption: sim.consumption || '',
      current_tariff: sim.current_tariff || '',
      discount_percentage: sim.discount_percentage || '',
      partner_commission: sim.partner_commission || '',
      total_bill: sim.total_bill || '',
      icms: sim.icms || '',
      pis: sim.pis || '',
      cofins: sim.cofins || '',
      public_lighting: sim.public_lighting || '',
      others: sim.others || ''
    });
    
    // Scroll para o formulário
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    toast.success("Todos os dados carregados! Edite e recalcule.");
  };

  const handleDeleteSimulation = async (simId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta simulação?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/simulations/${simId}`);
      toast.success("Simulação excluída com sucesso!");
      setSelectedIds(prev => prev.filter(id => id !== simId));
      fetchSimulations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir simulação");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos uma simulação para excluir");
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir ${selectedIds.length} simulação(ões)?`)) {
      return;
    }

    try {
      await Promise.all(selectedIds.map(id => axiosInstance.delete(`/simulations/${id}`)));
      toast.success(`${selectedIds.length} simulação(ões) excluída(s) com sucesso!`);
      setSelectedIds([]);
      fetchSimulations();
    } catch (error) {
      toast.error("Erro ao excluir simulações");
    }
  };

  const handleDeleteAll = async () => {
    if (simulations.length === 0) {
      toast.error("Não há simulações para excluir");
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir TODAS as ${simulations.length} simulações? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await axiosInstance.delete("/simulations/all");
      toast.success("Todas as simulações foram excluídas!");
      setSelectedIds([]);
      setCurrentPage(1);
      fetchSimulations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir simulações");
    }
  };

  const handleSelectAll = () => {
    const currentPageIds = paginatedSimulations.map(sim => sim.id);
    const allSelected = currentPageIds.every(id => selectedIds.includes(id));
    
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !currentPageIds.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...currentPageIds])]);
    }
  };

  const handleSelectOne = (simId) => {
    if (selectedIds.includes(simId)) {
      setSelectedIds(prev => prev.filter(id => id !== simId));
    } else {
      setSelectedIds(prev => [...prev, simId]);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error("Por favor, selecione um arquivo CSV");
      return;
    }

    setImporting(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axiosInstance.post("/simulations/import-csv", formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${response.data.imported} simulações importadas com sucesso!`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erros encontrados`);
      }
      fetchSimulations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao importar CSV");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportCSV = () => {
    if (!ensureCsvPassword()) return;
    if (simulations.length === 0) {
      toast.error("Não há simulações para exportar");
      return;
    }

    // Criar conteúdo CSV
    const headers = [
      'Data',
      'Cooperado',
      'Telefone',
      'Instalação',
      'Distribuidora',
      'Consumo (kWh)',
      'Tarifa (R$)',
      'Desconto (%)',
      'Total Fatura (R$)',
      'ICMS (R$)',
      'PIS (R$)',
      'COFINS (R$)',
      'Iluminação (R$)',
      'Outros (R$)',
      'Economia Verde (R$)',
      'Economia Amarela (R$)',
      'Economia Vermelha 1 (R$)',
      'Economia Vermelha 2 (R$)',
      'Economia Anual (R$)',
      'Parceiro'
    ];

    const rows = simulations.map(sim => [
      new Date(sim.created_at).toLocaleString('pt-BR'),
      sim.cooperative_name || '',
      sim.phone || '',
      sim.installation_number || '',
      sim.distributor || '',
      sim.consumption || 0,
      sim.current_tariff || 0,
      sim.discount_percentage || 0,
      sim.total_bill || 0,
      sim.icms || 0,
      sim.pis || 0,
      sim.cofins || 0,
      sim.public_lighting || 0,
      sim.others || 0,
      (sim.savings_green || 0).toFixed(2),
      (sim.savings_yellow || 0).toFixed(2),
      (sim.savings_red1 || 0).toFixed(2),
      (sim.savings_red2 || 0).toFixed(2),
      (sim.annual_savings || 0).toFixed(2),
      sim.partner_name || ''
    ]);

    // Usar ponto e vírgula como separador
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Criar blob com BOM para UTF-8 (compatibilidade Excel)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `simulacoes_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    toast.success("Histórico exportado com sucesso!");
  };

  const handleRegisterCooperative = (data) => {
    // Preparar TODOS os dados relevantes para o cadastro de cooperado
    const coopData = {
      name: data.cooperative_name || '',
      installation_number: data.installation_number || '',
      distributor: data.distributor || '',
      phone: data.phone || '',
      consumption: data.consumption || '',
      contracted_kwh: data.consumption || '',
      tariff_discount: data.discount_percentage || '',
      current_tariff: data.current_tariff || '',
      email: '',
      cpf_cnpj: '',
      registration_type: 'PF'
    };

    const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(coopData)))); // UTF-8 safe (acentos/Ç)
    navigate(`/cooperatives/new?data=${encodedData}`);
    toast.success("Redirecionando para cadastro de cooperado...");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  // Paginação
  const totalPages = Math.ceil(simulations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSimulations = simulations.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Verificar se admin
  const isAdmin = user?.role === 'admin';
  const cemigRule = getCemigRule(user?.email);
  const isCemig = formData.distributor === 'CEMIG';
  const discountEditable = isAdmin || isCemig;

  const handleCemigDiscountBlur = () => {
    if (!isCemig || isAdmin) return;
    const fixed = clampCemigDiscount(formData.discount_percentage, cemigRule);
    if (fixed !== null) {
      toast.warning(`Desconto CEMIG deve ficar entre ${cemigRule.min}% e ${cemigRule.max}%. Ajustado para ${fixed}%.`);
      setFormData(prev => ({ ...prev, discount_percentage: String(fixed) }));
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Simulador de Economia</h1>
          <p className="text-gray-600">Calcule a economia potencial de energia</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Simulation Form */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Nova Simulação
                </div>
                <div className="flex items-center gap-2">
                  <input
                    ref={aiFileInputRef}
                    type="file"
                    accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp"
                    className="hidden"
                    onChange={handleAIExtract}
                    data-testid="sim-ai-file-input"
                  />
                  <Button
                    type="button"
                    onClick={() => aiFileInputRef.current?.click()}
                    disabled={aiExtracting}
                    data-testid="sim-ai-extract-btn"
                    className="bg-white/15 hover:bg-white/25 border border-white/30 text-white text-xs h-8 px-3 backdrop-blur-sm"
                    title="Envie o PDF ou foto da fatura para preencher automaticamente"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                    {aiExtracting ? "Lendo…" : "Ler fatura (IA)"}
                  </Button>
                </div>
              </CardTitle>
              {aiFilledFields.length > 0 && (
                <div className="mt-2 text-[11px] text-white/90" data-testid="sim-ai-filled-summary">
                  ✨ IA preencheu {aiFilledFields.length} campo(s) — revise antes de calcular.
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSimulate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cooperative_name">Nome do Cooperado *</Label>
                  <Input
                    id="cooperative_name"
                    data-testid="sim-name-input"
                    type="text"
                    placeholder="Digite o nome"
                    value={formData.cooperative_name}
                    onChange={(e) => setFormData({ ...formData, cooperative_name: e.target.value })}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="installation_number">Número da Instalação *</Label>
                    <Input
                      id="installation_number"
                      data-testid="sim-installation-input"
                      type="text"
                      placeholder="Ex: 123456789"
                      value={formData.installation_number}
                      onChange={(e) => setFormData({ ...formData, installation_number: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone *</Label>
                    <Input
                      id="phone"
                      data-testid="sim-phone-input"
                      type="text"
                      placeholder="(00) 00000-0000"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="distributor">Distribuidora *</Label>
                  <Select value={formData.distributor} onValueChange={handleDistributorChange}>
                    <SelectTrigger data-testid="sim-distributor-select">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(distributorDiscounts).sort().map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.distributor && (
                    <p className="text-xs text-gray-500">
                      Desconto padrão: {distributorDiscounts[formData.distributor]}% | Tarifa Verde aplicada
                    </p>
                  )}
                </div>

                {!isWithTaxes(formData.distributor) && (
                <div className="space-y-2">
                  <Label htmlFor="compensation_type">Tipo de Compensação</Label>
                  <Select value={formData.compensation_type} onValueChange={handleCompensationTypeChange}>
                    <SelectTrigger data-testid="sim-compensation-type-select">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Geração Compartilhada">Geração Compartilhada</SelectItem>
                      <SelectItem value="Autoconsumo Remoto">Autoconsumo Remoto</SelectItem>
                      <SelectItem value="Autoconsumo C">Autoconsumo C</SelectItem>
                      {user?.role === 'admin' && (
                        <SelectItem value="Autoconsumo Remoto - Usina">Autoconsumo Remoto - Usina</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="consumption" className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-emerald-600" />
                      Consumo (kWh) *
                    </Label>
                    <Input
                      id="consumption"
                      data-testid="sim-consumption-input"
                      type="number"
                      step="0.01"
                      placeholder="Ex: 350"
                      value={formData.consumption}
                      onChange={(e) => setFormData({ ...formData, consumption: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="current_tariff" className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      Tarifa Atual (R$ por kWh) *
                    </Label>
                    <Input
                      id="current_tariff"
                      data-testid="sim-tariff-input"
                      type="number"
                      step="0.0001"
                      placeholder="Selecione a distribuidora"
                      value={formData.current_tariff}
                      onChange={(e) => setFormData({ ...formData, current_tariff: e.target.value })}
                      required
                    />
                    <p className="text-xs text-gray-500">
                      Preenchido com tarifa verde, mas pode ser editado
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="discount" className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                    % de Desconto *
                    {isAdmin && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        Admin: Editável
                      </span>
                    )}
                  </Label>
                  <Input
                    id="discount"
                    data-testid="sim-discount-input"
                    type="number"
                    step="0.1"
                    placeholder="Ex: 15"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                    onBlur={handleCemigDiscountBlur}
                    className={!discountEditable ? "bg-gray-100 cursor-not-allowed" : ""}
                    readOnly={!discountEditable}
                    required
                  />
                  <p className="text-xs text-gray-500" data-testid="sim-discount-hint">
                    {isAdmin
                      ? 'Você pode editar o desconto para todas as distribuidoras' 
                      : isCemig
                      ? `CEMIG: editável entre ${cemigRule.min}% e ${cemigRule.max}% (Comissão = ${cemigRule.max} − Desconto)`
                      : 'Automático conforme distribuidora selecionada'}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="total_bill">Total da Fatura (R$)</Label>
                  <Input
                    id="total_bill"
                    type="number"
                    step="0.01"
                    placeholder="Ex: 450.00"
                    value={formData.total_bill}
                    onChange={(e) => setFormData({ ...formData, total_bill: e.target.value })}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!isWithTaxes(formData.distributor) && !(formData.compensation_type || '').startsWith('Autoconsumo') && (
                    <div className="space-y-2">
                      <Label htmlFor="icms">{(formData.compensation_type || '').startsWith('Autoconsumo') ? 'ICMS TUSD (R$)' : 'ICMS (R$)'}</Label>
                      <Input
                        id="icms"
                        type="number"
                        step="0.01"
                        placeholder="Ex: 50.00"
                        value={formData.icms}
                        onChange={(e) => setFormData({ ...formData, icms: e.target.value })}
                      />
                    </div>
                  )}
                  
                  {!(formData.compensation_type || '').startsWith('Autoconsumo') && (
                  <div className="space-y-2">
                    <Label htmlFor="pis">PIS (R$)</Label>
                    <Input
                      id="pis"
                      type="number"
                      step="0.01"
                      placeholder="Ex: 10.00"
                      value={formData.pis}
                      onChange={(e) => setFormData({ ...formData, pis: e.target.value })}
                    />
                  </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!(formData.compensation_type || '').startsWith('Autoconsumo') && (
                  <div className="space-y-2">
                    <Label htmlFor="cofins">COFINS (R$)</Label>
                    <Input
                      id="cofins"
                      type="number"
                      step="0.01"
                      placeholder="Ex: 15.00"
                      value={formData.cofins}
                      onChange={(e) => setFormData({ ...formData, cofins: e.target.value })}
                    />
                  </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="public_lighting">Iluminação Pública (R$)</Label>
                    <Input
                      id="public_lighting"
                      type="number"
                      step="0.01"
                      placeholder="Ex: 20.00"
                      value={formData.public_lighting}
                      onChange={(e) => setFormData({ ...formData, public_lighting: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="others">Outros (R$)</Label>
                  <Input
                    id="others"
                    type="number"
                    step="0.01"
                    placeholder="Ex: 5.00"
                    value={formData.others}
                    onChange={(e) => setFormData({ ...formData, others: e.target.value })}
                  />
                </div>
                
                <Button
                  data-testid="sim-calculate-button"
                  type="submit"
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  disabled={calculating}
                >
                  {calculating ? "Calculando..." : "Calcular Economia"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Result Display */}
          {result && (
            <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardHeader>
                <CardTitle className="text-emerald-700 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Resultado da Simulação
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-white rounded-lg p-4 space-y-3">
                  <div className="pb-2 border-b">
                    <p className="text-xs text-gray-500">Cooperado</p>
                    <p className="font-semibold">{result.cooperative_name}</p>
                  </div>
                  
                  <div className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-600">Instalação:</span>
                    <span className="font-semibold">{result.installation_number}</span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-600">Distribuidora:</span>
                    <span className="font-semibold">{result.distributor}</span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-600">Consumo:</span>
                    <span className="font-semibold" data-testid="result-consumption">{result.consumption} kWh</span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-600">Desconto:</span>
                    <span className="font-semibold" data-testid="result-discount">{result.discount_percentage}%</span>
                  </div>
                  
                  {result.total_bill > 0 && (
                    <>
                      <div className="flex justify-between items-center pb-2 border-b">
                        <span className="text-gray-600">Total da Fatura:</span>
                        <span className="font-semibold">R$ {result.total_bill.toFixed(2)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center pb-2 border-b bg-blue-50 p-2 rounded">
                        <span className="text-gray-700 font-semibold">Economia da Fatura Atual:</span>
                        <span className="font-bold text-blue-600">R$ {result.current_bill_savings?.toFixed(2) || '0.00'}</span>
                      </div>
                    </>
                  )}
                  
                  <div className="pt-3 space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Economia por Bandeira:</p>
                    
                    {(() => {
                      const showPercentages = result.method ? result.method !== 'with_taxes' : !isWithTaxes(result.distributor);
                      const consumption = result.consumption || 0;
                      const currentTariff = result.current_tariff || 0;
                      const baseValue = consumption * currentTariff;
                      
                      const calcPercentage = (saving) => {
                        if (baseValue === 0) return 0;
                        return (saving / baseValue) * 100;
                      };
                      
                      return (
                        <>
                          <div className="flex justify-between items-center text-sm">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-green-500"></span>
                              Verde:
                            </span>
                            <span className="font-semibold">
                              R$ {result.savings_green?.toFixed(2) || '0.00'}
                              {showPercentages && ` (${calcPercentage(result.savings_green || 0).toFixed(1)}%)`}
                            </span>
                          </div>
                          
                          <div className="flex justify-between items-center text-sm">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                              Amarela:
                            </span>
                            <span className="font-semibold">
                              R$ {result.savings_yellow?.toFixed(2) || '0.00'}
                              {showPercentages && ` (${calcPercentage(result.savings_yellow || 0).toFixed(1)}%)`}
                            </span>
                          </div>
                          
                          <div className="flex justify-between items-center text-sm">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-red-400"></span>
                              Vermelha 1:
                            </span>
                            <span className="font-semibold">
                              R$ {result.savings_red1?.toFixed(2) || '0.00'}
                              {showPercentages && ` (${calcPercentage(result.savings_red1 || 0).toFixed(1)}%)`}
                            </span>
                          </div>
                          
                          <div className="flex justify-between items-center text-sm">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-red-600"></span>
                              Vermelha 2:
                            </span>
                            <span className="font-semibold">
                              R$ {result.savings_red2?.toFixed(2) || '0.00'}
                              {showPercentages && ` (${calcPercentage(result.savings_red2 || 0).toFixed(1)}%)`}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  
                  <div className="flex justify-between items-center pt-3 mt-3 border-t bg-emerald-100 rounded-lg p-3">
                    <span className="text-lg font-semibold text-emerald-800">Economia Anual:</span>
                    <span className="text-2xl font-bold text-emerald-700" data-testid="result-annual">
                      R$ {result.annual_savings.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    6 meses verde + 2 amarela + 1 vermelha 1 + 3 vermelha 2
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    data-testid="download-pdf-button"
                    onClick={() => handleDownloadPDF(result.id)}
                    className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar PDF
                  </Button>
                  
                  <Button
                    data-testid="register-coop-button"
                    onClick={() => handleRegisterCooperative(result)}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Cadastrar Cooperado
                  </Button>
                </div>
                
                <p className="text-xs text-gray-500 text-center">
                  Parceiro: {result.partner_name}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* History */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <CardTitle>Histórico de Simulações ({simulations.length})</CardTitle>
            <div className="flex gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? "Importando..." : "Importar CSV"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={simulations.length === 0}
                className="border-green-500 text-green-600 hover:bg-green-50"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
              {selectedIds.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteSelected}
                  className="border-red-500 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir ({selectedIds.length})
                </Button>
              )}
              {isAdmin && simulations.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteAll}
                  className="border-red-500 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Todas
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Carregando...</div>
            ) : simulations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma simulação realizada ainda.
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={paginatedSimulations.length > 0 && paginatedSimulations.every(sim => selectedIds.includes(sim.id))}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Cooperado</TableHead>
                      <TableHead>Consumo (kWh)</TableHead>
                      <TableHead>Desconto (%)</TableHead>
                      <TableHead>Economia Anual</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedSimulations.map((sim) => (
                      <TableRow key={sim.id} data-testid={`sim-row-${sim.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(sim.id)}
                            onCheckedChange={() => handleSelectOne(sim.id)}
                          />
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(sim.created_at)}</TableCell>
                        <TableCell>{sim.cooperative_name}</TableCell>
                        <TableCell>{sim.consumption}</TableCell>
                        <TableCell>{sim.discount_percentage}%</TableCell>
                        <TableCell className="font-bold text-emerald-700">
                          R$ {sim.annual_savings.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              data-testid={`register-coop-sim-${sim.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRegisterCooperative(sim)}
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              title="Cadastrar Cooperado"
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`edit-sim-${sim.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditSimulation(sim)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Editar Simulação"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`download-sim-${sim.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPDF(sim.id)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Baixar PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`delete-sim-${sim.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSimulation(sim.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Excluir Simulação"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      Mostrando {startIndex + 1} - {Math.min(startIndex + itemsPerPage, simulations.length)} de {simulations.length} simulações
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => goToPage(pageNum)}
                            className={currentPage === pageNum ? "bg-emerald-600" : ""}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
